import {Component, OnDestroy} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import {
  participantsWeightList
} from '../../../core/admin-groups-list.selectors';
import {
  roles, sortOrderMap,
  statusLabelMap, userStatusMap
} from '../../../../../shared/constants/common.constants';
import {CommonService} from '../../../../../shared/services/common.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ParticipantAndCaregiver
} from '../../../../../../models/participantAndCaregiver';
import {
  ParticipantsWeightList
} from '../../../../../../models/participants-weight-list';
import {
  AdminGroupListService
} from '../../../services/admin-group-list.service';
import {Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {
  FetchParticipantsWeightListRequest
} from '../../../core/admin-groups-list.actions';
import {
  drillDownUrls,
  profile,
  selectedUserProfile
} from '../../../../../shared/core/shared.selectors';
import {SetDrillDownUrls} from '../../../../../shared/core/shared.actions';

@Component({
  selector: 'app-participant-weight-information-table',
  templateUrl: './participant-weight-information-table.component.html',
  styleUrls: ['./participant-weight-information-table.component.css']
})
export class ParticipantWeightInformationTableComponent implements OnDestroy {
  loading: boolean = false;

  typeMap = {
    4: 'Adult',
    5: 'Participant'
  };
  participantCaregiverList: ParticipantsWeightList;
  displayedColumns = [
    'name',
    'group',
    'currentWeight',
    'weightChange',
    'weightLossAlert',
    'currentBMI',
    'changeBMI'
  ];

  participantOnlyColumns = [
    'currentMedianBMI',
    'changeMedianBMI',
    'currentOverPercentile',
    'changeOverPercentile'
  ]

  tableData: any[];
  dataSource: any = new MatTableDataSource();
  role: number;
  roles = roles;
  userRole: number;
  sortOrder: any = 1;
  sortColumn: any = 'name'
  isFirstPage = true;
  selectedUserId: number | null;
  selectedUserRole: number | null;
  protected readonly clinicStatusLabelMap = statusLabelMap;
  private readonly OnDestroy: Subject<any> = new Subject<any>();
  private participantWeightFilters: {
    clientId?: number[],
    groupId?: number[],
    role?: number[],
    session?: number,
    status?: number[],
    searchText?: string,
  } = {
    role: [roles.participant],
    session: 1
  };
  user;
  selectedUser;
  clientId;

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private adminGroupListService: AdminGroupListService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    if (this.route.snapshot.paramMap.get('clientId')) {
      this.clientId = this.route.snapshot.paramMap.get('clientId');
      this.fetchParticipantWeightList();
    } else if (this.route.snapshot.paramMap.get('clientAdminId')) {
      this.store.select(profile)
        .pipe(takeUntil(this.OnDestroy))
        .subscribe(data => {
          this.user = data;
        });

      this.store.select(selectedUserProfile)
        .pipe(takeUntil(this.OnDestroy))
        .subscribe(data => {
          if (data.profile) {
            this.selectedUser = data?.profile;
            let clientId = this.user.clientId;

            if (this.user?.role && this.selectedUser?.role) {
              clientId = this.selectedUser.clientId;
            }
            this.clientId = clientId
            this.fetchParticipantWeightList();
          }
        });
    } else {
      this.fetchParticipantWeightList();
    }

    this.store.select(participantsWeightList)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.participantCaregiverList = data;

        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0);

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0);
          }
        }
      });

    // listen for filter changes from the filter component
    this.adminGroupListService.onParticipantWeightInformationFilterChange.pipe(takeUntil(this.OnDestroy))
      .subscribe((filters) => {
        this.isFirstPage = true;

        this.displayedColumns = this.displayedColumns.filter(column => !this.participantOnlyColumns.includes(column));

        if (filters['role']?.length && filters['role'].includes(roles.participant)) {
          this.displayedColumns = [
            ...this.displayedColumns,
            ...this.participantOnlyColumns
          ]
        }
        this.participantWeightFilters = filters;
        this.fetchParticipantWeightList();
      });
  }

  fetchParticipantWeightList(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchParticipantsWeightListRequest({
      payload: {
        ...this.participantWeightFilters,
        pageNumber,
        limit,
        ...(this.sortOrder ? {sortOrder: this.sortOrder} : {}),
        ...(this.sortOrder ? {sortColumn: this.sortColumn} : {}),
        ...(this.clientId ? {clientId: this.clientId} : {})
      }
    }));
  }

  onScroll() {
    if (this.participantCaregiverList?.hasNext && !this.participantCaregiverList?.loading) {
      this.fetchParticipantWeightList(this.participantCaregiverList.pageNumber + 1, this.participantCaregiverList.limit);
      this.isFirstPage = false;
    }
  }

  openUserDetails(user: ParticipantAndCaregiver) {
    if (user?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];

      this.router.navigate([`/${urlPrefix}/member/${user.id}/behavior-tracker`]);
    }
  }

  groupNameClick(data) {
    if (data?.group?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`${urlPrefix}/group/${data.group.id}/dashboard`])
    }
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];

    this.fetchParticipantWeightList();
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

  protected readonly userStatusMap = userStatusMap;
}
